<template>
    <div class="p-6">
      <h1 class="text-3xl font-bold text-gray-900 dark:text-gray-50 mb-6 flex items-center">
        <button
          @click="$router.push(`/binary/?tab=${formField.binary_type}`)"
          class="mr-4 bg-blue-600 text-white px-4 py-1.5 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition duration-200 text-sm font-medium flex items-center"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-4 w-4 mr-1"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fill-rule="evenodd"
              d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
              clip-rule="evenodd"
            />
          </svg>
          Back to Binaries
        </button>
        {{ isNewBinary ? "Add Binary" : "Edit Binary" }}
        <div class="relative group">
        <button
            type="button"
            class="hover:bg-transparent bg-transparent ml-2"
            @click="startTutorial"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-circle-question-mark-icon lucide-circle-question-mark dark:text-gray-50 text-gray-900"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/></svg>
        </button>
          <span class="absolute top-full text-xs right-1/2 -translate-y-1/2 mb-1 px-1.5 py-0.5 text-xs text-white bg-gray-800 dark:bg-gray-700 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">Tutorial</span>
        </div>
      </h1>
  
      <div v-if="loading" class="text-center py-4">
        <svg
          class="animate-spin h-8 w-8 mr-2 inline-block dark:text-gray-50 text-gray-900"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            class="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            stroke-width="4"
          ></circle>
          <path
            class="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 2.21.896 4.21 2.342 5.658l1.658-1.367z"
          ></path>
        </svg>
      </div>

      <HowToBinaries v-if="showHowToPage"
        :show-how-to-page="showHowToPage"
        :external-navigation="true"
        @show-how-to-close="handleShowHowToPage"
      />

      <!-- Change Binary Modal -->
      <div v-if="changingItem">
        <div 
          @click.self="changingItem=false"  
          class="flex items-center justify-center inset-0 fixed bg-black/50 backdrop-blur-sm z-40">
          <div class="glass-card p-6 w-[60%] m-auto shadow-xl items-center text-center">
            <div class="mb-4 text-base flex space-x-2 justify-between">
              <span class="font-semibold">Are you sure you want to change this binary?</span>
              <button
                @click="changingItem=false"
                class="bg-gray-300 hover:bg-gray-400 dark:hover:bg-gray-800 text-black dark:text-white px-2 py-2 bg-gray-200 dark:bg-gray-700 rounded-full"
            >
                <svg xmlns="http://www.w3.org/2000/svg" 
                width="24" 
                height="24" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                stroke-width="2" 
                stroke-linecap="round" 
                stroke-linejoin="round" 
                class="lucide lucide-x-icon lucide-x"><path 
                d="M18 6 6 18"/><path d="m6 6 12 12"/>
                </svg>
            </button>
            </div>
            <div class="overflow-auto w-[90%] h-[70%] m-auto items-center text-center mb-4 bg-gray-100 dark:bg-gray-800 rounded">
              <p class="font-semibold mt-2">This change will affect your workspace configurations and may impact:</p>
              <ul class="my-3">
                <li class="font-medium justify-left flex pl-4">Active data collection processes.</li>
                <li class="font-medium justify-left flex pl-4">Linked workspace, launcher and collector configurations.</li>
              </ul>
            </div>
            <div class="flex justify-center space-x-4">
              
              <div class="text-base flex space-x-2 justify-between">
                <button 
                  class="btn border border-transparent m text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  @click="proceedWithFileUpload">
                  Accept
                </button>
                <button 
                  class="btn border border-transparent text-white bg-gray-500 hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                  @click="changingItem=false">
                  Cancel
                </button>
              </div>
            </div>
          </div>  
        </div>
      </div>
  
      <div
        v-else-if="error"
        class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative"
        role="alert"
      >
        <strong class="font-bold">Error!</strong>
        <span class="block sm:inline">{{ error }}</span>
      </div>
  
      <form v-else @submit.prevent="saveBinary" class="space-y-6 p-6 glass-card">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label
              for="license_state"
              class="block text-sm font-medium text-gray-700 dark:text-white"
              >Binary Type <span class="text-red-500 font-bold text-base">*</span></label
            >
            <GlassSelect
              id="binary_type"
              v-model="formField.binary_type"
              :options="binaryFormatting"
              placeholder="Select a Binary Type"
              :valueKey="'key'"
              :labelKey="'label'"
              @focus="clearFieldError('binary_type')"
              :error="!!fieldErrors['binary_type']"
            />
            <FieldError :message="fieldErrors['binary_type']"/>
          </div>

          <div v-if="isNewBinary">
            <label for="operating_system" class="block text-sm font-medium text-gray-700 dark:text-white">Operating System <span class="text-red-500 font-bold text-base">*</span></label>
            <GlassSelect
              id="operating_system"
              v-model="formField.operating_system"
              @focus="clearFieldError('operating_system')"
              :options="os_options"
              valueKey="os_type"
              labelKey="os_type"
              placeholder="Select an Operating System"
              :error="!!fieldErrors['operating_system']"
            />
            <FieldError :message="fieldErrors['operating_system']"/>
          </div>

          <div v-if="!isNewBinary">
            <label class="block text-sm font-medium text-gray-700 dark:text-white">Operating System</label>
            <input
              type="text"
              readonly
              tabindex="-1"
              :value="formField.operating_system"
              class="mt-1 block w-full pl-3 pr-10 py-2 text-base border border-gray-300 sm:text-sm rounded-md bg-gray-100 dark:bg-gray-700 dark:text-white cursor-default pointer-events-none"
            />
          </div>

          <div v-if="isNewBinary">
            <label for="architecture" class="block text-sm font-medium text-gray-700 dark:text-white">Architecture <span class="text-red-500 font-bold text-base">*</span></label>
            <GlassSelect
              id="architecture"
              v-model="formField.architecture"
              @focus="clearFieldError('architecture')"
              :options="arch_options"
              valueKey="arch"
              labelKey="arch"
              placeholder="Select an Architecture"
              :error="!!fieldErrors['architecture']"
            />
            <FieldError :message="fieldErrors['architecture']"/>
          </div>

          <div v-if="!isNewBinary">
            <label class="block text-sm font-medium text-gray-700 dark:text-white">Architecture</label>
            <input
              type="text"
              readonly
              tabindex="-1"
              :value="formField.architecture"
              class="mt-1 block w-full pl-3 pr-10 py-2 text-base border border-gray-300 sm:text-sm rounded-md bg-gray-100 dark:bg-gray-700 dark:text-white cursor-default pointer-events-none"
            />
          </div>

          <div v-if="isNewBinary && spaceTypeSelectable">
            <label for="space_type" class="block text-sm font-medium text-gray-700 dark:text-white">Space Type <span class="text-red-500 font-bold text-base">*</span></label>
            <GlassSelect
              id="space_type"
              v-model="formField.space_type"
              @focus="clearFieldError('space_type')"
              :options="space_type_options"
              valueKey="value"
              labelKey="label"
              placeholder="Select a Space Type"
              :error="!!fieldErrors['space_type']"
            />
            <FieldError :message="fieldErrors['space_type']"/>
          </div>

          <div v-if="!isNewBinary">
            <label class="block text-sm font-medium text-gray-700 dark:text-white">Space Type</label>
            <input
              type="text"
              readonly
              tabindex="-1"
              :value="formField.space_type"
              class="mt-1 block w-full pl-3 pr-10 py-2 text-base border border-gray-300 sm:text-sm rounded-md bg-gray-100 dark:bg-gray-700 dark:text-white cursor-default pointer-events-none"
            />
          </div>

          <div>
            <label
              for="organization"
              class="block text-sm font-medium text-gray-700 dark:text-white"
              >Organisation <span class="text-red-500 font-bold text-base">*</span></label
            >
            <GlassSelect
              id="organization"
              v-model="formField.organization"
              :options="orgs"
              placeholder="Select an Organisation"
              @focus="clearFieldError('organization')"
              :error="!!fieldErrors['organization']"
            />
            <FieldError :message="fieldErrors['organization']"/>
          </div>

          <div>
            <label for="file"  class="block text-sm font-medium text-gray-700 dark:text-white">
              File <span class="text-red-500 font-bold text-base">*</span>
            </label>

            <input
              type="file"
              id="file"
              ref="file"
              @change="handleFileUpload"
              class="hidden"
            />
            <div :class="[ 'flex space-x-2 w-full border border-gray-500/70 dark:bg-gray-700 bg-gray-200 rounded focus:outline-none focus:ring-2', fieldErrors['file']
                  ? 'ring-2 ring-red-500  focus:ring-red-500'
                  : 'focus:ring-blue-500'
            ]">
              <button
                type="button"
                id="choose_file"
                @click="showFileChangeWarning"
                :disabled="isProcessingFile"
                class="shrink-0 px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-indigo-600"
              >
                Choose File
              </button>
              <div v-if="isProcessingFile" class="flex items-center mt-2 text-sm text-gray-500 dark:text-gray-300">
                <svg class="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 2.21.896 4.21 2.342 5.658l1.658-1.367z" />
                </svg>
                Validating file...
              </div>
              <div v-else-if="formField.file" class="flex items-center flex-1 min-w-0 mt-2">
                <a class="truncate hover:no-underline dark:hover:text-indigo-300 dark:text-white" :href="formField.file" target="_blank">{{ formField.file?.name || formField.file}}</a>
                <button
                  v-if="isNewBinary"
                  type="button"
                  title="Clear selected file"
                  @click="clearSelectedFile"
                  class="shrink-0 ml-auto mr-2 bg-transparent hover:bg-transparent focus:bg-transparent text-gray-400 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-500 hover:scale-125 transition-all duration-150"
                >
                  <Trash2/>
                </button>
              </div>
            </div>
            <FieldError :message="fieldErrors['file']"/>
          </div>

          <div>
            <label
              for="group"
              class="block text-sm font-medium text-gray-700 dark:text-white"
              >Agent Group</label
            >
            <GlassSelect
              id="group"
              v-model="formField.group"
              :options="filteredGroups"
              placeholder="Select an Agent Group"
              @focus="clearFieldError('group')"
              :error="!!fieldErrors['group']"
            />
            <FieldError :message="fieldErrors['group']"/>
          </div>

          <div>
            <label
              for="agent"
              class="block text-sm font-medium text-gray-700 dark:text-white"
              >Agent</label
            >
            <GlassSelect
              id="agent"
              v-model="formField.agent"
              :options="filteredAgents"
              placeholder="Select an Agent"
              :valueKey="'licensekey'"
              :labelKey="'licensekey'"
              @focus="clearFieldError('agent')"
              :error="!!fieldErrors['agent']"
            />
            <FieldError :message="fieldErrors['agent']"/>
            <FieldError :message="fieldErrors['non_field_errors']"/>
          </div>
        </div>
        
        <div class="flex justify-end space-x-3">
          <button
            type="button"
            @click="$router.push(`/binary/?tab=${formField.binary_type}`)"
            class="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Cancel
          </button>
          <button v-if="isStaff || canChangeBinary || canAddBinary"
            type="submit"
            class="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            {{ isNewBinary ? "Create Binary" : "Save Changes" }}
          </button>
        </div>
      </form>
    </div> 
  </template>
  
  <script>
  import { createBinary, 
    getBinarysById, 
    updateBinary, 
    updateFile, 
    updateUsers, 
    createBinaryChunks, 
    initializeStaging 
  } 
    from "@/services/binaryService";
  import { getOrgs } from "@/services/wsOrgService";
  import { getAgentGroups } from "@/services/agentGroupService";
  import { getAgents } from "@/services/agentService";
  import { useStore } from "@/store";
  import FieldError from "@/components/FieldError.vue";
  import { getWorkspaceUsers } from "@/services/workspaceUsersService";
  import HowToBinaries from "./HowToBinaries.vue";
  import { useTutorial } from "@/composables/tutorial";
  import GlassSelect from "@/components/GlassSelect.vue";
  import { validateBinarySignature } from "@/utils/fileSignature";
  import { Trash2 } from "lucide-vue-next";


  export default {
    name: "BinaryDetails",
    setup() {
      const { start } = useTutorial();
      return { _start: start };
    },
    components: {
      FieldError,
      HowToBinaries,
      GlassSelect,
      Trash2
    },
    data() {
      return {
        formField: {
          binary_type: "",
          checksum: "",
          file: null,
          organization: null,
          group: null,
          agent: null,
          approved: false,
          status: "initial",
          users: null,
          operating_system: "",
          architecture: "",
          space_type: "",
        },
        showHowToPage: false,
        loading: false,
        isProcessingFile: false,
        error: null,
        fieldErrors: {},
        isNewBinary: false,
        orgs: [],
        agents: [],
        groups: [],
        binary_types : ['workspace', 'launcher', 'collector', 'alloy', 'alloyService'],
        os_options: [
          { os_type: "Windows" },
          { os_type: "Linux" },
          { os_type: "Mac" }
        ],
        arch_options: [
          { arch: "x86_64" },
          { arch: "arm64" },
        ],
        space_type_options: [
          { value: "workspace",   label: "Workspace" },
          { value: "serverspace", label: "Serverspace" },
        ],
        canChangeBinary: false,
        canAddBinary: false,
        changingItem: false,
        fileReference: null,
        workspaceUsers: null,
        steps: [
          {
            element: '#binary_type',
            action: 'select',
            value: 'launcher',
            message: 'Choose the binary type (Workspace, Launcher, or Collector).'
          },
          {
            element: '#operating_system',
            action: 'select',
            value: 'Windows',
            message: 'Choose the operating system for this binary (Linux, Windows, or Mac).'
          },
          {
            element: '#architecture',
            action: 'select',
            value: 'x86_64',
            message: 'Choose the architecture thios binary is built for.'
          },
          {
            element: '#organization', 
            action: 'select',
            value: 1,
            message: 'Select the organisation for this binary.',
          },
          {
            element: '#choose_file',
            action: 'display',
            value: null,
            message: 'Click "Choose File" to upload your binary file.'
          },
          {
            element: '#group',
            action: 'select',
            value: 1,
            message: 'Select the agent group to assign this binary to (optional).'
          },
          {
            element: '#agent',
            action: 'select',
            value: 'SAMPLE-LICENSE-KEY',
            message: 'Select a specific agent (optional).'
          },
          {
            element: 'button[type="submit"]',
            action: 'display',
            value: null,
            message: 'Click to create the binary or save your changes.'
          }
        ],
      };
    },
    computed: {
      isStaff(){
        return useStore.isStaff();
      },
      user() {
        return useStore.getUser();
      }, 
      formattedBinary() {
        return {
          ...this.formField,
        };
      },
      binaryFormatting(){
        return this.binary_types.map(item => ({
            key : item,
            label: `${item[0].toUpperCase()}${item.slice(1)}`
        }));
      },
      spaceTypeSelectable(){
        return this.formField.operating_system === 'Windows';
      },
      filteredAgents() {
        if (!this.formField.organization) return [];
        return this.agents.filter(a => a.organization === this.formField.organization);
      },
      filteredGroups() {
        if (!this.formField.organization) return [];
        return this.groups.filter(g => g.organization === this.formField.organization);
      }
    },
    watch: {
      user: {
        handler(user) {
          if (user) {
            this.checkPermissions();
          }
        },
        immediate: true
      },
      'formField.organization'() {
        this.formField.agent = null;
        this.formField.group = null;
      },
      async 'formField.operating_system'(newOs) {
        this.applySpaceTypeRules();
        const file = this.formField.file;
        if (!newOs || !file || typeof file === 'string') return;
        const result = await validateBinarySignature(file, newOs);
        if (!result.ok) {
          this.fieldErrors = { ...this.fieldErrors, file: result.error };
          if (this.$refs.file) this.$refs.file.value = '';
          this.formField.file = null;
        }
      },
      'formField.binary_type'() {
        this.applySpaceTypeRules();
      }
    },
    async created() {
      const binaryId = this.$route.params.id;
      this.checkPermissions();
      
      if (binaryId === "new") {
        this.isNewBinary = true;
      } else {
        await this.fetchBinary(binaryId);
      }
      await this.fetchWorkspaceUsers();
      await this.fetchOrg();
      await this.fetchAgents();
      await this.fetchGroups();
    },
    methods: {
      clearFieldError(fieldName){
        if (this.fieldErrors[fieldName]) {
          delete this.fieldErrors[fieldName];
          delete this.fieldErrors['non_field_errors'];
        }
      },
      validateBinaryData(){
        this.fieldErrors = {};
        if (this.formField.organization === null){
          this.fieldErrors['organization'] = "Please select an organisation"
        }
        if (this.formField.binary_type === ""){
          this.fieldErrors['binary_type'] = "Please select a binary type"
        }
        if (this.isNewBinary && this.formField.operating_system === ""){
          this.fieldErrors['operating_system'] = "Please select an operating system"
        }
        if (this.isNewBinary && this.spaceTypeSelectable && this.formField.space_type === ""){
          this.fieldErrors['space_type'] = "Please select a space type"
        }
        if (this.formField.file === null || this.formField.file === ""){
          this.fieldErrors['file'] = "Please upload a binary file"
        }
      },
      applySpaceTypeRules(){

        if (!this.isNewBinary) return;
     
        if (!this.spaceTypeSelectable) {
          this.formField.space_type = 'serverspace';
        }
      },
      handleShowHowToPage(value){
        this.showHowToPage = value;
      },
      async fetchWorkspaceUsers(){
        try {
          const response = await getWorkspaceUsers();
          this.workspaceUsers = response;
        } catch (error) {
          console.error("Users:", error);
        }
      },
      checkPermissions(){
        this.canChangeBinary = useStore.hasPermission("Can change binaries");
        this.canAddBinary = useStore.hasPermission("Can add binaries");
      },
      showFileChangeWarning() {
        if (this.formField.file && !this.isNewConfig) {
          this.changingItem = true;
          this.fileReference = this.$refs.file;
        } else {
          this.$refs.file.click();
        }
      },
      proceedWithFileUpload() {
        this.changingItem = false;
        this.fileReference.click();
      },
      async handleFileUpload(event) {
        this.clearFieldError('file');
        const file = event.target.files[0];
        if (!file) return;

        this.isProcessingFile = true;
        try {
          const result = await validateBinarySignature(file, this.formField.operating_system);
          if (!result.ok) {
            this.fieldErrors = { ...this.fieldErrors, file: result.error };
            if (this.$refs.file) this.$refs.file.value = '';
            this.formField.file = null;
            return;
          }
          this.formField.file = file;
        } finally {
          this.isProcessingFile = false;
        }
      },
      clearSelectedFile() {
        this.formField.file = null;
        if (this.$refs.file) this.$refs.file.value = '';
        this.clearFieldError('file');
      },
      async fetchOrg(){
        try {
          this.orgs = await getOrgs();
        } catch (error) {
          console.error("Error fetching organization:", error);
        }
      },
      async fetchAgents(){
        try {
          this.agents = await getAgents();
        } catch (error) {
          console.error("Error fetching agents:", error);
        }
      },
      async fetchGroups(){
        try {
          this.groups = await getAgentGroups();
        } catch (error) {
          console.error("Error fetching groups:", error);
        }
      },
      async fetchBinary(binaryId) {
        this.loading = true;
        this.error = null;
        try {
          this.formField =  await getBinarysById(binaryId);
        } catch (error) {
          console.error("Error fetching binary:", error);
          this.error =
            "An error occurred while fetching binary details. Please try again later.";
        } finally {
          this.loading = false;
        }
      },
      async urlToFile(url, filename, mimeType) {
        const response = await fetch(url);
        const blob = await response.blob();
        return new File([blob], filename, { type: mimeType || blob.type });
      },
      async saveBinary() {

        this.validateBinaryData();
        if (Object.keys(this.fieldErrors).length > 0) return;

        this.loading = true;
        this.error = null;
        
        try {

          const binaryData = {...this.formattedBinary,
            agent: this.formattedBinary.agent === null ? "" : this.formattedBinary.agent,
            organization: this.formattedBinary.organization === null ? "" : this.formattedBinary.organization,
            group: this.formattedBinary.group === null ? "" : this.formattedBinary.group,
          };

          if (this.isNewBinary) {
            delete binaryData.file
            const binary = await createBinary(binaryData);
            await this.sendFileChunks(binary.id, false) // false = staging
          } else {
            const users = await updateUsers(this.formField.id, {users: this.workspaceUsers})

            const noFileUpload = !(binaryData.file instanceof File)
            if (noFileUpload){
              delete binaryData.file
              await updateBinary(this.formField.id, binaryData);
            } else {

              await initializeStaging(this.formField.id, { isChunked: true });
              await this.sendFileChunks(this.formField.id, true); // true = staging
            }
          }

          this.$router.push({ name: "BinaryView" });

        } catch (error) {
          this.fieldErrors = {};
          const errData = error?.response?.data;
          if (errData) {
            for (const key in errData) {
              const value = errData[key];
  
              if (Array.isArray(value)) {
                this.fieldErrors[key] = value[0];
              } 
              else if (typeof value === 'object' && value !== null) {
                const nestedFirstKey = Object.keys(value)[0];
                const nestedMessages = value[nestedFirstKey];
  
                if (Array.isArray(nestedMessages)) {
                  this.fieldErrors[key] = nestedMessages[0];
                }
              }
            }
          }
  
          console.error("Error saving organization:", error);
        } finally {
          this.loading = false;
        }
      },
      async sendFileChunks(uploadId, isStaging = false){
        const file = this.formField.file;
        const chunkSize = 50 * 1024 * 1024;
        const totalChunks = Math.ceil(file.size / chunkSize);
        
        for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
          const start = chunkIndex * chunkSize;
          const end = Math.min(start + chunkSize, file.size);
          const chunk = file.slice(start, end);
          
          const formData = new FormData();
          formData.append('chunk', chunk);
          formData.append('uploadId', uploadId);
          formData.append('chunkIndex', chunkIndex);
          formData.append('totalChunks', totalChunks);
          formData.append('isStaging', isStaging);
          
          try {
            await createBinaryChunks(uploadId, formData);
          } catch (error) {
            console.error(`Failed to upload chunk ${chunkIndex}:`, error);
          }
          
        }
      },
      async startTutorial(){
        const originalOrgs = [...this.orgs];
        const originalGroups = [...this.groups];
        const originalAgents = [...this.agents];
        const originalFormField = { ...this.formField };

        this.orgs = [{id: 1, name: 'Tutorial Organisation'}];
        this.groups = [{id: 1, name: 'Tutorial Agent Group'}];
        this.agents = [{licensekey: 'TUTORIAL-LICENSE-KEY-123', name: 'Tutorial Agent'}];
        
        await this._start(this.steps, () => {
          this.orgs = originalOrgs;
          this.groups = originalGroups;
          this.agents = originalAgents;
          this.formField = originalFormField;
        });
      },
      async sendFileChunks(uploadId, isStaging = false){
        const file = this.formField.file;
        const chunkSize = 50 * 1024 * 1024;
        const totalChunks = Math.ceil(file.size / chunkSize);
        
        for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
          const start = chunkIndex * chunkSize;
          const end = Math.min(start + chunkSize, file.size);
          const chunk = file.slice(start, end);
          
          const formData = new FormData();
          formData.append('chunk', chunk);
          formData.append('uploadId', uploadId);
          formData.append('chunkIndex', chunkIndex);
          formData.append('totalChunks', totalChunks);
          formData.append('isStaging', isStaging);
          
          try {
            await createBinaryChunks(uploadId, formData);
          } catch (error) {
            console.error(`Failed to upload chunk ${chunkIndex}:`, error);
          }
          
        }
      }
    },
  };
  </script>
  